import { useTheme } from '@mui/material';
import { useHistory } from 'react-router';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTheme } from '../../../../hooks/use-get-theme';
import { useSettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import { useIsInitialDataLoaded } from '../../../../components/initial-page-load-handler/initial-page-load-handler';
import { useGetPageConditionRequirementResults } from '../../../../app/pages-requiring-condition';
import { useGetCurrentPage } from '../../../../app/get-current-page';
import { Page } from '../../../../app/page-url-path';
import { useGetRouteToIssuerPage } from '../../../../app/get-route-to-page';
import { useBuildersToProcessGqlData } from '../../../../dotcms/use-builders-to-process-gql-data';
import { useFetchIssuerDotcmsContentWithDefaultFallback } from '../../../../dotcms/use-fetch-from-dotcms-with-default-fallback';
import { LoginInfoGqlHelper } from '../../../../dotcms/dotcms-models/dotcms-login-info';
import { GlobalLoadingStateOperation } from '../../../../components/abc/global-loading-context/global-loading-context';
import { issuerSelector } from '../../../../selectors';
import { useStringUrlSearchParam } from '../../../../utils/url-search-params/use-query-url-search-params';
import UrlSearchParamKey from '../../../../utils/url-search-params/url-search-params';
import type { LoginMethod } from '../../../../selectors/interfaces';
import { AllowedInternalLoginMethods } from './use-login-dob-form';
import type { IAlertData } from '../../../../dotcms/dotcms-models/dotcms-alert';
import { AlertDisplayConditionTime } from '../../../../dotcms/dotcms-models/dotcms-alert-enums';
import { convertAbsoluteUrlToRelativeUrl } from '../../../../app/router-utils';
import { useLoginNavigation } from './use-login-navigation';

const useLoginPage = () => {
  const theme = useGetTheme();
  const muiTheme = useTheme();
  const { isMobile, isTabletMini } = useSettingBasedOnBreakpoints(muiTheme);

  const { login } = useSelector(issuerSelector);
  const isInitialDataLoaded = useIsInitialDataLoaded();
  const { getLinkToInternalMainLoginPage } = useLoginNavigation();

  const { createGqlQueryForSingle, processGqlDataAsSingle, contentTypeName } =
    useBuildersToProcessGqlData(LoginInfoGqlHelper);
  const { data, loading, error } =
    useFetchIssuerDotcmsContentWithDefaultFallback({
      buildGqlSubQuery: createGqlQueryForSingle,
      processResponse: processGqlDataAsSingle,
      loadingStateOperationName:
        GlobalLoadingStateOperation.LOGIN_INFO_DOTCMS_FETCH,
      contentTypeName,
    });

  const history = useHistory();
  const routeToHomePage = useGetRouteToIssuerPage(Page.HOMEPAGE);
  const mainLoginPageUrl = getLinkToInternalMainLoginPage();
  const routeToLoginPage = convertAbsoluteUrlToRelativeUrl(
    mainLoginPageUrl.toString(),
  );
  const currentPage = useGetCurrentPage();
  const isPageAvailable =
    useGetPageConditionRequirementResults()[currentPage ?? Page.LOGIN_PAGE];

  useEffect(() => {
    if (!isPageAvailable) {
      history.push(routeToHomePage);
    }
  }, [isPageAvailable, history, routeToHomePage]);

  const loginMethodRaw = useStringUrlSearchParam(
    UrlSearchParamKey.LOGIN_METHOD,
  );

  const loginMethod: LoginMethod | undefined = useMemo(() => {
    if (!loginMethodRaw) {
      return undefined;
    }

    const matchingSearchParamValue = AllowedInternalLoginMethods.find(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      (x) => x === loginMethodRaw,
    );
    if (matchingSearchParamValue === undefined) {
      console.error(
        `The ${
          UrlSearchParamKey.LOGIN_METHOD
        } url search param key did not match any of the expected and allowed LoginMethod enum values. The value we received was: ${
          loginMethodRaw ?? ''
        }`,
      );

      return undefined;
    }

    if (
      isInitialDataLoaded &&
      login?.supported_login_methods?.find(
        (x) => x === matchingSearchParamValue,
      ) === undefined
    ) {
      console.error(
        `The ${
          UrlSearchParamKey.LOGIN_METHOD
        } url search param key did not match any of the LoginMethod enum values supported for the issuer. The value we received was: ${
          matchingSearchParamValue ?? ''
        }`,
      );

      return undefined;
    }

    return matchingSearchParamValue;
  }, [loginMethodRaw, isInitialDataLoaded, login?.supported_login_methods]);

  // This banner should only show if we have a temporary message that we would like to convey.
  const filterAlerts = (alert: IAlertData) =>
    !![
      AlertDisplayConditionTime.SHOW_ALWAYS,
      AlertDisplayConditionTime.CUSTOM_DATE,
    ].find((x) => alert.displayConditionsTime.includes(x));

  return {
    theme,
    muiTheme,
    isMobile,
    isTabletMini,
    data,
    loading,
    error,
    isInitialDataLoaded,
    loginMethod,
    routeToLoginPage,
    filterAlerts,
  };
};

export default useLoginPage;
